<template>
    <div class="col-lg-12">
        <div class="col-12">
            <el-tabs
                v-model="activeTab"
                class="invoice-tabs"
                type="border-card"
            >
                <el-tab-pane label="Unpaid Invoices" name="one">
                    <table-search-and-pagination
                        :per-page="invoicesPerPage"
                        :table-data="invoices"
                        @perPageUpdated="
                            $emit('invoicesPerPageUpdated', $event)
                        "
                        @dataUpdated="queriedInvoices = $event"
                    >
                        <div slot="header" class="d-flex space-between">
                            <h3 class="card-title mr-3">
                                Outstanding Invoices
                                <span :class="$style.totalUnpaid">
                                    ({{
                                        showFilterOptions
                                            ? formatter.format(outstandingTotal)
                                            : totalUnpaidAmount
                                    }})
                                </span>
                            </h3>
                            <h3
                                v-if="showFilterOptions"
                                class="card-title mr-3"
                            >
                                Write-Offs
                                <span :class="$style.totalUnpaid">
                                    ({{ formatter.format(writeOffTotal) }})
                                </span>
                            </h3>
                            <h3 v-if="showFilterOptions" class="card-title">
                                In Collections
                                <span :class="$style.totalUnpaid">
                                    ({{ formatter.format(collectionsTotal) }})
                                </span>
                            </h3>
                        </div>
                        <div
                            v-if="showFilterOptions"
                            slot="filters"
                            class="ml-3"
                        >
                            <div :class="$style.filterGroup">
                                <div :class="$style.filterLabel">
                                    Filter Status
                                </div>
                                <div :class="$style.filterOptions">
                                    <el-checkbox-group
                                        v-model="paymentStatusFilter"
                                        size="medium"
                                        @change="filtersChanged"
                                    >
                                        <el-checkbox border label="1">
                                            Outstanding
                                        </el-checkbox>
                                        <el-checkbox border label="2">
                                            Collections
                                        </el-checkbox>
                                        <el-checkbox border label="4">
                                            Write Off
                                        </el-checkbox>
                                    </el-checkbox-group>
                                </div>
                            </div>
                        </div>
                        <invoices-table
                            v-if="invoices.length"
                            :exclude-columns="excludeColumns"
                            :invoices="queriedInvoices"
                            :past-due-amount-for-days="pastDueAmountForDays"
                            :current-due-amount="currentDueAmount"
                            @downloadFile="$emit('downloadFile', $event)"
                            @payInvoices="payInvoices"
                        />
                        <h4 v-else>
                            You have no unpaid invoices matching your filters:
                            {{
                                paymentStatusFilter
                                    .map((f) => paymentStatusLabel(f))
                                    .join(", ")
                            }}
                        </h4>
                    </table-search-and-pagination>
                </el-tab-pane>
                <el-tab-pane label="Paid Invoices" name="two">
                    <table-search-and-pagination
                        v-if="paidInvoicesTabLoaded"
                        title="Paid Invoices"
                        :per-page="paidInvoicesPerPage"
                        :table-data="paidInvoices"
                        @perPageUpdated="
                            $emit('paidInvoicesPerPageUpdated', $event)
                        "
                        @dataUpdated="queriedPaidInvoices = $event"
                    >
                        <paid-invoices-table
                            v-if="paidInvoices.length"
                            :exclude-columns="excludeColumns"
                            :invoices="queriedPaidInvoices"
                            @downloadFile="$emit('downloadFile', $event)"
                        />
                        <h4 v-else>You have no paid invoices.</h4>
                    </table-search-and-pagination>
                </el-tab-pane>
            </el-tabs>
        </div>

        <invoice-payment
            v-if="invoicesToPay.length"
            :invoices="invoicesToPay"
            :show="showPaymentModal"
            @update:show="updateInvoicePaymentModal"
        />
    </div>
</template>

<script>
import moment from "moment-timezone";
import {
    Button,
    Popover,
    Checkbox,
    CheckboxGroup,
    Tag,
    Tabs,
    TabPane,
} from "element-ui";
import TableSearchAndPagination from "src/components/TableSearchAndPagination";
import InvoicePayment from "../../InvoicePayment";
import InvoicesTable from "./InvoicesTable";
import PaidInvoicesTable from "./PaidInvoicesTable";

export default {
    name: "Invoices",
    components: {
        TableSearchAndPagination,
        InvoicesTable,
        PaidInvoicesTable,
        InvoicePayment,
        [Button.name]: Button,
        [Popover.name]: Popover,
        [Checkbox.name]: Checkbox,
        [CheckboxGroup.name]: CheckboxGroup,
        [Tag.name]: Tag,
        [Tabs.name]: Tabs,
        [TabPane.name]: TabPane,
    },
    props: {
        showFilterOptions: {
            type: Boolean,
            required: true,
        },
        paidInvoices: {
            type: Array,
            required: true,
        },
        invoices: {
            type: Array,
            required: true,
        },
        invoicesPerPage: {
            type: Number,
            required: true,
        },
        paidInvoicesPerPage: {
            type: Number,
            required: true,
        },
        outstandingTotal: {
            type: Number,
            required: true,
        },
        writeOffTotal: {
            type: Number,
            required: true,
        },
        collectionsTotal: {
            type: Number,
            required: true,
        },
        excludeColumns: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            showPaid: false,
            invoicesToPay: [],
            activeTab: "one",
            paidInvoicesTabLoaded: false,
            showPaymentModal: false,
            filtersVisible: false,
            PaymentStatusOutstanding: 1,
            PaymentStatusOutstandingCollections: 2,
            PaymentStatusPaid: 3,
            PaymentStatusWriteOff: 4,
            queriedPaidInvoices: [],
            queriedInvoices: [],
            paymentStatusFilter: ["1", "2", "4"],
            formatter: new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
            }),
        };
    },
    computed: {
        totalUnpaidAmount() {
            const amount = this.invoices
                .map((i) => i.total + this.interestApplied(i))
                .reduce((a, b) => a + b, 0);
            return this.formatter.format(amount);
        },
        filterTags() {
            const tags = [];
            if (this.paymentStatusFilter.length) {
                this.paymentStatusFilter.forEach((status) => {
                    tags.push({
                        name: this.paymentStatusLabel(status),
                        value: status,
                        group: "PaymentStatus",
                    });
                });
            }
            return tags;
        },
        currentDueAmount() {
            const now = moment();
            const invoices = this.invoices.filter((i) => {
                const daysPast = Math.floor(now.diff(i.due_date, "days", true));
                return daysPast <= 0;
            });

            const amount = invoices
                .map((i) => i.total + this.interestApplied(i))
                .reduce((a, b) => a + b, 0);
            return this.formatter.format(amount);
        },
    },
    watch: {
        activeTab(newVal) {
            if (newVal === "two") {
                if (!this.paidInvoicesTabLoaded) {
                    this.$emit("fetchPaidInvoices");
                }
                this.paidInvoicesTabLoaded = true;
            }
        },
    },
    methods: {
        paymentStatusLabel(label) {
            switch (Number(label)) {
                case this.PaymentStatusOutstanding:
                    return "Outstanding";
                case this.PaymentStatusOutstandingCollections:
                    return "Collections";
                case this.PaymentStatusPaid:
                    return "Paid";
                case this.PaymentStatusWriteOff:
                    return "Write Off";
                default:
                    return "Unknown";
            }
        },
        removeFilterTag(tag) {
            switch (tag.group) {
                case "PaymentStatus":
                    this.paymentStatusFilter.splice(
                        this.paymentStatusFilter.indexOf(tag.value),
                        1,
                    );
                    break;
                default:
                    break;
            }
            this.filtersChanged();
        },
        filtersChanged() {
            this.$emit("filtersChanged", this.paymentStatusFilter);
        },
        updateInvoicePaymentModal(show) {
            if (!show) this.invoicesToPay = false;
        },
        interestApplied(invoice) {
            if (invoice.apply_interest) {
                return invoice.interest_due;
            }

            return 0;
        },
        payInvoices(invoices) {
            this.invoicesToPay = invoices;
            this.showPaymentModal = true;
        },
        pastDueAmountForDays(min, max) {
            const now = moment();
            const invoices = this.invoices.filter((i) => {
                const daysPast = Math.floor(now.diff(i.due_date, "days", true));
                if (max) {
                    return daysPast > min && daysPast <= max;
                }
                return daysPast > min;
            });

            const amount = invoices
                .map((i) => i.total + this.interestApplied(i))
                .reduce((a, b) => a + b, 0);
            return this.formatter.format(amount);
        },
    },
};
</script>

<style>
.el-tag.el-tag--info.el-tag--light {
    background-color: #ecf5ff !important;
    color: #409eff !important;
    border: 1px solid #d9ecff;
}

.el-checkbox__label {
    text-transform: none;
}
</style>

<style module>
.totalUnpaid {
    font-size: 18px;
    color: gray;
}

.filterGroup {
    margin-bottom: 10px;
}

.filterLabel {
    font-size: 15px;
    font-weight: 600;
    margin-bottom: 5px;
}

.filterFooter {
    padding: 8px;
    background: #f3f7fd;
    margin: 0 -12px -12px;
    display: flex;
    justify-content: flex-end;
    border-top: 1px solid #a8c8f4;
}
</style>

<style>
.invoice-tabs .el-tabs__content {
    padding: 0px;
}
</style>
